import {ChangeDetectionStrategy, Component, Inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {take} from 'rxjs/operators';

/** One row of a mobile action sheet. Labels arrive translated, so the sheet stays resource-agnostic. */
export interface MobileSheetAction {
    label: string;
    /** Material Icons ligature, e.g. 'edit'. */
    icon?: string;
    /** Heading this action is listed under; consecutive actions with the same group share one heading. */
    group?: string;
    disabled?: boolean;
    /** Printed under a disabled action - touch has no tooltip to explain why it is greyed out. */
    reason?: string;
    tone?: 'danger';
    run: () => void;
}

export interface MobileSheetData {
    title?: string;
    actions: MobileSheetAction[];
}

/**
 * The phone stand-in for a desktop dropdown or right-click menu: the same actions as 52px labelled rows in
 * a bottom sheet (every nz-modal is one on a phone, see _mobile-shell.scss). Styles: _mobile-kit.scss.
 *
 * Open it through MobileActionSheetService rather than directly.
 */
@Component({
    selector: 'di-mobile-action-sheet',
    standalone: true,
    imports: [CommonModule, TranslateModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="di-msheet">
            <div class="di-msheet-title" *ngIf="data.title">{{ data.title }}</div>
            <ng-container *ngFor="let action of data.actions; let i = index">
                <div class="di-msheet-group" *ngIf="action.group && action.group !== data.actions[i - 1]?.group">
                    {{ action.group }}
                </div>
                <button
                    type="button"
                    class="di-msheet-row"
                    [disabled]="action.disabled"
                    [attr.data-tone]="action.tone || null"
                    (click)="choose(action)">
                    <span class="di-mi" *ngIf="action.icon">{{ action.icon }}</span>
                    <span class="di-msheet-label">
                        {{ action.label }}
                        <span class="di-msheet-reason" *ngIf="action.disabled && action.reason">{{ action.reason }}</span>
                    </span>
                </button>
            </ng-container>
            <button type="button" class="di-msheet-cancel" (click)="close()">{{ 'mobileKit.cancel' | translate }}</button>
        </div>
    `
})
export class DiMobileActionSheetComponent {
    constructor(
        @Inject(NZ_MODAL_DATA) public readonly data: MobileSheetData,
        private readonly _modalRef: NzModalRef
    ) {}

    /**
     * The sheet is closed before the action runs, so an action that opens its own modal (Reject, Split,
     * Resource...) opens on a clean screen instead of stacking on top of the sheet.
     */
    choose(action: MobileSheetAction): void {
        if (action.disabled) {
            return;
        }
        this._modalRef.afterClose.pipe(take(1)).subscribe(() => action.run());
        this._modalRef.close();
    }

    close(): void {
        this._modalRef.close();
    }
}
